/**
 * M09 Knowledge 管理 shell · 索引状态、文档维护、检索沙盒、命中率。
 */
import { Play, Plus, Trash2, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";

import { type RuleSurfaceRow, RuleSurfaceSummary } from "@/components/shared/rule-surface-summary";
import { SingleStageRuntimePanel } from "@/components/shared/single-stage-runtime-panel";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import {
	type KnowledgeContentType,
	type KnowledgeDocumentItem,
	type KnowledgeSearchHit,
	useCreateKnowledgeDocument,
	useDeleteKnowledgeDocument,
	useKnowledgeDocuments,
	useKnowledgeIndexStatus,
	useKnowledgePrefetchMetrics,
	useKnowledgeSearchSandbox,
} from "@/lib/api/knowledge";

const DEFAULT_MERCHANT_ID = "mer_devcons";

const KNOWLEDGE_RULE_ROWS: RuleSurfaceRow[] = [
	{
		name: "知识源",
		source: "文档入库、namespace、索引状态",
		edit: "可新增和删除文档",
		effect: "条件生效",
		verify: "先看索引状态，再跑检索与验证",
		tone: "partial",
	},
	{
		name: "检索验证",
		source: "知识搜索沙盒",
		edit: "可调整问题和知识分组",
		effect: "仅沙盒",
		verify: "命中片段可用，不代表最终回复已经正确引用",
		tone: "sandbox",
	},
	{
		name: "主链路知识召回",
		source: "Playbook pre_fetch_spec + RAG runtime",
		edit: "规则在处理方案里改，文档在知识源里改",
		effect: "主链路生效",
		verify: "链路测试台看 rag_completed、知识片段数和 evidence_refs",
		tone: "live",
	},
];

export type M09View = "sources" | "search-verify";

export function M09Shell({ view = "sources" }: { view?: M09View }) {
	const [merchantId, setMerchantId] = useState(DEFAULT_MERCHANT_ID);
	const [namespace, setNamespace] = useState("policy");
	const [contentType, setContentType] = useState<KnowledgeContentType>("markdown");
	const [title, setTitle] = useState("Shipping policy live note");
	const [bodyText, setBodyText] = useState("物流政策：测试店铺订单发货后会同步 tracking number。");
	const [docStatus, setDocStatus] = useState<string | null>(null);
	const [query, setQuery] = useState("物流政策");

	const indexStatus = useKnowledgeIndexStatus(merchantId);
	const documents = useKnowledgeDocuments(merchantId);
	const metrics = useKnowledgePrefetchMetrics(merchantId);
	const createDoc = useCreateKnowledgeDocument();
	const deleteDoc = useDeleteKnowledgeDocument(merchantId);
	const searchSandbox = useKnowledgeSearchSandbox(merchantId);

	const activeDocs = documents.data?.items ?? [];
	const latestHitRate = metrics.data ? `${Math.round(metrics.data.latest_hit_rate * 100)}%` : "0%";
	const docCollection = useMemo(
		() => indexStatus.data?.collections.find((item) => item.purpose === "documents") ?? null,
		[indexStatus.data?.collections],
	);
	// 真探活后任一 collection 异常/未建即视为向量检索不可用（S8 诚实受限）。
	const unavailableCollection = useMemo(
		() =>
			indexStatus.data?.collections.find(
				(item) => item.status === "error" || item.status === "not_configured",
			) ?? null,
		[indexStatus.data?.collections],
	);

	function handleCreateDocument() {
		setDocStatus(null);
		createDoc.mutate(
			{
				merchant_id: merchantId,
				namespace,
				title,
				content_type: contentType,
				body_text: bodyText,
				updated_by: "admin-console",
			},
			{
				onSuccess: (data) => {
					setDocStatus(`已入库 · ${data.chunk_count} chunk`);
				},
				onError: (error) => {
					setDocStatus(error instanceof Error ? error.message : "入库失败");
				},
			},
		);
	}

	function handleSearch() {
		searchSandbox.mutate({
			merchant_id: merchantId,
			query,
			namespaces: ["policy", "faq", "product_kb"],
			top_k: 5,
		});
	}

	return (
		<div className="space-y-4" data-testid="m09-shell">
			<RuleSurfaceSummary
				rows={KNOWLEDGE_RULE_ROWS}
				testId="knowledge-rule-surface-summary"
				description="这个页面管知识内容和检索验证；某次应答是否引用知识，还要看处理路径匹配、namespace 和本轮证据结果。"
			/>

			<div className="grid gap-3 md:grid-cols-4">
				<Metric label="本地文档" value={indexStatus.data?.local_document_count ?? 0} />
				<Metric
					label="文档索引"
					value={docCollection?.collection_name ?? "documents_dev"}
					compact
				/>
				<Metric label="沙盒查询" value={metrics.data?.latest_total_searches ?? 0} />
				<Metric label="命中率" value={latestHitRate} />
			</div>

			{view === "sources" ? (
				<>
					<section
						id="knowledge-index-status"
						className="scroll-mt-6 grid gap-3 rounded-md border border-surface-border p-4 lg:grid-cols-[260px_minmax(0,1fr)]"
						data-testid="m09-index-status"
					>
						<div className="space-y-3">
							<label className="grid gap-1 text-sm">
								<span className="text-xs text-text-muted">商户 ID</span>
								<input
									value={merchantId}
									onChange={(event) => setMerchantId(event.target.value)}
									className="h-9 rounded-md border border-surface-border bg-surface-background px-3 font-mono text-sm"
								/>
							</label>
							<div className="text-xs text-text-secondary">
								向量检索：{vectorSearchStatusLabel(indexStatus.data)}
							</div>
						</div>
						<div className="overflow-x-auto">
							<table className="w-full border-collapse text-sm">
								<thead className="bg-surface-subtle">
									<tr className="text-left">
										<th className="px-3 py-2 font-medium">用途</th>
										<th className="px-3 py-2 font-medium">索引集合</th>
										<th className="px-3 py-2 font-medium">状态</th>
										<th className="px-3 py-2 font-medium">本地文档</th>
										<th className="px-3 py-2 font-medium">向量点数</th>
									</tr>
								</thead>
								<tbody>
									{(indexStatus.data?.collections ?? []).map((item) => (
										<tr key={item.purpose} className="border-t border-surface-border">
											<td className="px-3 py-2">{collectionPurposeLabel(item.purpose)}</td>
											<td className="px-3 py-2 font-mono">{item.collection_name}</td>
											<td className="px-3 py-2">
												<span className={collectionStatusClass(item.status)} title={item.message}>
													{collectionStatusLabel(item.status)}
												</span>
											</td>
											<td className="px-3 py-2 font-mono">{item.local_document_count}</td>
											<td className="px-3 py-2 font-mono">{item.points_count ?? "-"}</td>
										</tr>
									))}
								</tbody>
							</table>
							{unavailableCollection ? (
								<div
									className="mt-3 flex gap-2 rounded-md border border-brand-danger/30 bg-surface-subtle px-3 py-2 text-xs leading-5 text-text-secondary"
									data-testid="m09-index-degraded"
								>
									<TriangleAlert
										className="mt-0.5 h-4 w-4 flex-none text-brand-danger"
										aria-hidden="true"
									/>
									<span>
										向量检索不可用（{collectionStatusLabel(unavailableCollection.status)}）：
										{unavailableCollection.message} 知识问答当前退本地词法 /
										静态兜底，结果不代表线上 RAG。
									</span>
								</div>
							) : null}
						</div>
					</section>

					<section
						id="knowledge-sources"
						className="scroll-mt-6 grid gap-4 rounded-md border border-surface-border p-4 xl:grid-cols-[380px_minmax(0,1fr)]"
					>
						<div className="space-y-3">
							<div className="grid gap-2 sm:grid-cols-2">
								<div className="grid gap-1 text-sm">
									<span className="text-xs text-text-muted">知识分组</span>
									<Select
										className="w-full"
										aria-label="知识分组"
										value={namespace}
										onChange={(event) => setNamespace(event.target.value)}
									>
										<option value="policy">政策 policy</option>
										<option value="faq">常见问题 FAQ</option>
										<option value="product_kb">商品知识 product_kb</option>
									</Select>
								</div>
								<div className="grid gap-1 text-sm">
									<span className="text-xs text-text-muted">内容类型</span>
									<Select
										className="w-full"
										aria-label="内容类型"
										value={contentType}
										onChange={(event) => setContentType(event.target.value as KnowledgeContentType)}
									>
										<option value="markdown">Markdown</option>
										<option value="pdf">PDF</option>
										<option value="word">Word</option>
										<option value="text">Text</option>
									</Select>
								</div>
							</div>
							<label className="grid gap-1 text-sm">
								<span className="text-xs text-text-muted">标题</span>
								<input
									value={title}
									onChange={(event) => setTitle(event.target.value)}
									className="h-9 rounded-md border border-surface-border bg-surface-background px-3 text-sm"
									data-testid="m09-doc-title"
								/>
							</label>
							<label className="grid gap-1 text-sm">
								<span className="text-xs text-text-muted">内容</span>
								<textarea
									value={bodyText}
									onChange={(event) => setBodyText(event.target.value)}
									className="min-h-32 rounded-md border border-surface-border bg-surface-background p-3 text-sm"
									data-testid="m09-doc-body"
								/>
							</label>
							<div className="flex items-center gap-3">
								<Button type="button" onClick={handleCreateDocument} data-testid="m09-doc-create">
									<Plus className="mr-2 h-4 w-4" />
									入库
								</Button>
								<span className="text-sm text-text-secondary" data-testid="m09-doc-status">
									{createDoc.isPending ? "入库中..." : docStatus}
								</span>
							</div>
						</div>
						<DocumentTable
							items={activeDocs}
							onDelete={(docId) => deleteDoc.mutate(docId)}
							deleting={deleteDoc.isPending}
						/>
					</section>
				</>
			) : null}

			{view === "search-verify" ? (
				<>
					<SingleStageRuntimePanel
						kind="knowledge"
						targetStage="knowledge_retrieval"
						sampleId="P10TA005"
						sampleMessage="超过 30 天还能退货吗"
						expectedIntent="return_request"
						expectedPlaybook="PB03_return"
						assetName="知识文档 / 检索配置 / 证据引用"
						boundary="当前验证知识片段能否被找回，不代表最终回复一定正确引用。"
					/>
					<section
						id="knowledge-search-verify"
						className="scroll-mt-6 grid gap-4 rounded-md border border-surface-border p-4 lg:grid-cols-[minmax(320px,0.75fr)_minmax(0,1.25fr)]"
					>
						<div className="space-y-3">
							<label className="grid gap-1 text-sm">
								<span className="text-xs text-text-muted">检索问题</span>
								<textarea
									value={query}
									onChange={(event) => setQuery(event.target.value)}
									className="min-h-24 rounded-md border border-surface-border bg-surface-background p-3 text-sm"
									data-testid="m09-search-query"
								/>
							</label>
							<Button type="button" onClick={handleSearch} data-testid="m09-search-run">
								<Play className="mr-2 h-4 w-4" />
								检索
							</Button>
							<div className="grid grid-cols-3 gap-2 text-sm" data-testid="m09-metrics">
								<InlineMetric label="查询" value={metrics.data?.latest_total_searches ?? 0} />
								<InlineMetric label="命中" value={metrics.data?.buckets.at(-1)?.hit_count ?? 0} />
								<InlineMetric label="命中率" value={latestHitRate} />
							</div>
						</div>
						<SearchResults
							items={searchSandbox.data?.items ?? []}
							loading={searchSandbox.isPending}
						/>
					</section>
				</>
			) : null}
		</div>
	);
}

function DocumentTable({
	items,
	onDelete,
	deleting,
}: {
	items: KnowledgeDocumentItem[];
	onDelete: (docId: string) => void;
	deleting: boolean;
}) {
	return (
		<div className="overflow-x-auto rounded-md border border-surface-border">
			<table className="w-full border-collapse text-sm" data-testid="m09-doc-table">
				<thead className="bg-surface-subtle">
					<tr className="text-left">
						<th className="px-3 py-2 font-medium">标题</th>
						<th className="px-3 py-2 font-medium">知识分组</th>
						<th className="px-3 py-2 font-medium">类型</th>
						<th className="px-3 py-2 font-medium">分片</th>
						<th className="px-3 py-2 font-medium">操作</th>
					</tr>
				</thead>
				<tbody>
					{items.map((item) => (
						<tr key={item.doc_id} className="border-t border-surface-border align-top">
							<td className="max-w-md px-3 py-2">
								<div className="font-medium">{item.title}</div>
								<div className="mt-1 line-clamp-2 text-xs text-text-secondary">
									{item.body_preview}
								</div>
							</td>
							<td className="px-3 py-2">{namespaceLabel(item.namespace)}</td>
							<td className="px-3 py-2">{contentTypeLabel(item.content_type)}</td>
							<td className="px-3 py-2 font-mono">{item.chunk_count}</td>
							<td className="px-3 py-2">
								<Button
									type="button"
									variant="ghost"
									size="icon"
									disabled={deleting}
									onClick={() => onDelete(item.doc_id)}
									aria-label={`删除 ${item.title}`}
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function SearchResults({ items, loading }: { items: KnowledgeSearchHit[]; loading: boolean }) {
	return (
		<div
			className="min-h-60 rounded-md border border-surface-border bg-surface-background p-3"
			data-testid="m09-search-results"
		>
			{loading ? (
				<div className="text-sm text-text-secondary">检索中...</div>
			) : items.length === 0 ? (
				<div className="text-sm text-text-secondary">未运行</div>
			) : (
				<div className="space-y-3">
					{items.map((item) => (
						<div key={item.doc_id} className="border-b border-surface-border pb-3 last:border-0">
							<div className="flex items-center justify-between gap-3">
								<div className="font-medium">{item.title}</div>
								<div className="font-mono text-xs text-text-secondary">{item.score.toFixed(2)}</div>
							</div>
							<div className="mt-1 text-xs text-text-muted">{namespaceLabel(item.namespace)}</div>
							<div className="mt-2 text-sm text-text-secondary">{item.content}</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

function collectionPurposeLabel(purpose: string): string {
	if (purpose === "documents") return "文档知识";
	if (purpose === "products") return "商品知识";
	if (purpose === "chunks") return "知识分片";
	return purpose;
}

function collectionStatusLabel(status: string): string {
	if (status === "configured") return "未探活";
	if (status === "not_configured") return "未建集合";
	if (status === "empty") return "空集合";
	if (status === "ok") return "可用";
	if (status === "error") return "异常";
	return status;
}

function collectionStatusClass(status: string): string {
	const base = "inline-flex w-fit rounded-full border px-2 py-0.5 text-xs font-medium";
	if (status === "ok") return `${base} border-surface-border bg-surface-card text-brand-success`;
	if (status === "empty") return `${base} border-surface-border bg-surface-card text-brand-warning`;
	if (status === "error" || status === "not_configured")
		return `${base} border-surface-border bg-surface-card text-brand-danger`;
	// configured(未探活) / 未知：中性，不充当绿灯。
	return `${base} border-surface-border bg-surface-subtle text-text-muted`;
}

function vectorSearchStatusLabel(
	data: { qdrant_enabled: boolean; collections: { status: string }[] } | undefined,
): string {
	if (!data) return "加载中…";
	if (!data.qdrant_enabled) return "未配置";
	if (data.collections.some((item) => item.status === "ok")) return "已接入（探活通过）";
	if (data.collections.some((item) => item.status === "error" || item.status === "not_configured"))
		return "已配置但探活失败";
	if (data.collections.some((item) => item.status === "empty")) return "已接入（集合为空）";
	return "已配置";
}

function namespaceLabel(namespace: string): string {
	if (namespace === "policy") return "政策";
	if (namespace === "faq") return "常见问题";
	if (namespace === "product_kb") return "商品知识";
	return namespace;
}

function contentTypeLabel(type: string): string {
	if (type === "markdown") return "Markdown";
	if (type === "pdf") return "PDF";
	if (type === "word") return "Word";
	if (type === "text") return "纯文本";
	return type;
}

function Metric({
	label,
	value,
	compact = false,
}: { label: string; value: number | string; compact?: boolean }) {
	return (
		<div className="rounded-md border border-surface-border bg-surface-card px-4 py-3">
			<div className="text-xs text-text-muted">{label}</div>
			<div className={`mt-1 font-mono font-semibold ${compact ? "text-lg" : "text-2xl"}`}>
				{value}
			</div>
		</div>
	);
}

function InlineMetric({ label, value }: { label: string; value: number | string }) {
	return (
		<div className="rounded-md bg-surface-subtle px-3 py-2">
			<div className="text-xs text-text-muted">{label}</div>
			<div className="mt-1 font-mono font-semibold">{value}</div>
		</div>
	);
}
